/* ... existing imports ... */
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiX, FiSave, FiAlertCircle } = FiIcons

const TradeForm = ({ trade, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    symbol: '',
    side: 'buy',
    entry_price: '',
    exit_price: '',
    stop_loss: '',
    take_profit: '',
    quantity: '',
    entry_time: '',
    exit_time: '',
    setup_type: '',
    notes: '',
    emotion_entry: 'neutral',
    emotion_exit: 'neutral',
    pnl: ''
  })
  const [error, setError] = useState('')

  useEffect(() => {
    if (trade) {
      setFormData({
        symbol: trade.symbol || '',
        side: trade.side || 'buy',
        entry_price: trade.entry_price?.toString() || '',
        exit_price: trade.exit_price?.toString() || '',
        stop_loss: trade.stop_loss?.toString() || '',
        take_profit: trade.take_profit?.toString() || '',
        quantity: trade.quantity?.toString() || '',
        entry_time: trade.entry_time ? new Date(trade.entry_time).toISOString().slice(0, 16) : '',
        exit_time: trade.exit_time ? new Date(trade.exit_time).toISOString().slice(0, 16) : '',
        setup_type: trade.setup_type || '',
        notes: trade.notes || '',
        emotion_entry: trade.emotion_entry || 'neutral',
        emotion_exit: trade.emotion_exit || 'neutral',
        pnl: trade.pnl?.toString() || ''
      })
    }
  }, [trade])

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    try {
      if (!formData.symbol) throw new Error('Symbol is required')
      const entryPrice = parseFloat(formData.entry_price)
      const exitPrice = parseFloat(formData.exit_price)
      const quantity = parseFloat(formData.quantity)

      let calculatedPnL = parseFloat(formData.pnl)
      if (isNaN(calculatedPnL)) {
        if (!isNaN(entryPrice) && !isNaN(exitPrice) && !isNaN(quantity)) {
          if (formData.side === 'buy') {
            calculatedPnL = (exitPrice - entryPrice) * quantity
          } else {
            calculatedPnL = (entryPrice - exitPrice) * quantity
          }
        } else {
          calculatedPnL = 0
        }
      }

      const tradeData = {
        symbol: formData.symbol,
        side: formData.side,
        entry_price: isNaN(entryPrice) ? null : entryPrice,
        exit_price: isNaN(exitPrice) ? null : exitPrice,
        stop_loss: parseFloat(formData.stop_loss) || null,
        take_profit: parseFloat(formData.take_profit) || null,
        quantity: isNaN(quantity) ? null : quantity,
        pnl: isNaN(calculatedPnL) ? 0 : calculatedPnL,
        entry_time: formData.entry_time ? new Date(formData.entry_time).toISOString() : new Date().toISOString(),
        exit_time: formData.exit_time ? new Date(formData.exit_time).toISOString() : null,
        setup_type: formData.setup_type || 'Unknown',
        notes: formData.notes || '',
        emotion_entry: formData.emotion_entry || 'neutral',
        emotion_exit: formData.emotion_exit || 'neutral'
      }
      onSubmit(tradeData)
    } catch (err) {
      setError(err.message)
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto"
    >
      <motion.div 
        initial={{ scale: 0.9 }} 
        animate={{ scale: 1 }} 
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-8"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{trade ? 'Edit Trade' : 'Add New Trade'}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><SafeIcon icon={FiX} /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Symbol *</label>
                <input type="text" value={formData.symbol} onChange={(e) => handleChange('symbol', e.target.value)} className="w-full px-3 py-2 border rounded-lg" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Side</label>
                <select value={formData.side} onChange={(e) => handleChange('side', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Entry Price (Rp)</label>
                <input type="number" step="any" value={formData.entry_price} onChange={(e) => handleChange('entry_price', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exit Price (Rp)</label>
                <input type="number" step="any" value={formData.exit_price} onChange={(e) => handleChange('exit_price', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                <input type="number" step="any" value={formData.quantity} onChange={(e) => handleChange('quantity', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">P&L (Rp)</label>
              <input 
                type="number" 
                step="any" 
                value={formData.pnl} 
                onChange={(e) => handleChange('pnl', e.target.value)} 
                placeholder="Leave blank to auto-calculate (Entry - Exit) * Qty" 
                className="w-full px-3 py-2 border rounded-lg bg-gray-50 focus:bg-white transition-colors" 
              />
            </div>
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <SafeIcon icon={FiSave} className="h-4 w-4" />
                <span>{trade ? 'Update' : 'Save'} Trade</span>
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default TradeForm